import React from "react";
import GenericDashboard from "../GenericDashboard/GenericDashboard";
import APIService from "../../Service/APIService";
import { Input, Flex, Spin, Button as AntButton } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import { colors } from "../../theme/colors";
import { hasPermission } from "../../Config/permissions.js";
import { useAuth } from "../AuthProvider.js";
import ASUserAddAction from "../ASUsers/ASUserAddAction.js";
import { canAdminPerformUserAction } from "./userActionAccess.js";
import RestoreUserAction, { canRestoreUsers } from "./RestoreUserAction";

// Small rounded pill used for the Role / Region columns in the AS Users table,
// matching the presentation on the standalone AS Users page.
const Pill = ({ children }) => (
  <AntButton
    type="default"
    shape="round"
    size="small"
    style={{
      color: colors["primaryColor"],
      borderColor: colors["primaryColor"],
      width: "100%",
    }}
  >
    {children}
  </AntButton>
);

const formatName = (user) =>
  [user.FirstName, user.LastName].filter(Boolean).join(" ");

const mapAsUser = (user) => ({
  ...user,
  Name: formatName(user),
  RoleType: user.RoleType ? <Pill>{user.RoleType}</Pill> : null,
  Region: user.RegionName ? <Pill>{user.RegionName}</Pill> : null,
});

const UserDashboard = () => {
  const { user } = useAuth();
  const [selectedSegment, setSelectedSegment] = React.useState("AS Users");
  const [asUsers, setAsUsers] = React.useState([]);
  const [allAsUsers, setAllAsUsers] = React.useState([]);
  const [accountUsers, setAccountUsers] = React.useState([]);
  const [allAccUsers, setAllAccUsers] = React.useState([]);
  const [distributorUsers, setDistributorUsers] = React.useState([]);
  const [allDistUsers, setAllDistUsers] = React.useState([]);
  const [manufacturerUsers, setManufacturerUsers] = React.useState([]);
  const [allManufacturerUsers, setAllManufacturerUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const canViewUsers = hasPermission(user, "user.view");
  // Superadmins use Admin Tools; delegated admins retain their permission-based entry.
  const showRestore = canRestoreUsers(user) && !user?.IsSuperAdmin;

  const getUsersData = async () => {
    const apiServe = new APIService();

    // AS Users (admins & viewers) — same source as the AS Users page.
    await getASUsersData(apiServe);

    const response = await apiServe.getDataFromBackend(`getaccountusers`);
    const tempData = response?.data?.data?.map((user) => ({
      ...user,
      Name: formatName(user),
    }));
    setAccountUsers(tempData);
    setAllAccUsers(tempData);
    const response1 = await apiServe.getDataFromBackend(`getdistributorusers`);
    const tempData1 = response1?.data?.data?.map((user) => ({
      ...user,
      Name: formatName(user),
    }));
    setDistributorUsers(tempData1);
    setAllDistUsers(tempData1);
    const response2 = await apiServe.getDataFromBackend(`getmanufacturerusers`);
    const tempData2 = response2?.data?.data?.map((user) => ({
      ...user,
      Name: formatName(user),
    }));
    setManufacturerUsers(tempData2);
    setAllManufacturerUsers(tempData2);
  };

  const getASUsersData = async (apiService = new APIService()) => {
    const asResponse = await apiService.getDataFromBackend("getasusers");
    const asData = asResponse?.data?.data?.map(mapAsUser) ?? [];
    setAsUsers(asData);
    setAllAsUsers(asData);
  };

  React.useEffect(() => {
    if (!user) return;
    if (!canViewUsers) {
      setLoading(false);
      return;
    }
    getUsersData().then(() => {
      setLoading(false);
    });
  }, [canViewUsers, user]);

  const handleSegmentChange = (value) => {
    setSelectedSegment(value);
  };

  const handleSearch = async (val) => {
    if (selectedSegment === "AS Users") {
      // No dedicated AS user search endpoint — filter the loaded set locally.
      if (val === "") {
        setAsUsers(allAsUsers);
        return;
      }
      const term = val.toLowerCase();
      setAsUsers(
        allAsUsers.filter((user) =>
          [user.FirstName, user.LastName, user.Email, user.Phone]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term)),
        ),
      );
      return;
    }
    if (selectedSegment === "Manufacturer Users") {
      if (val === "") {
        setManufacturerUsers(allManufacturerUsers);
        return;
      }
      const term = val.toLowerCase();
      setManufacturerUsers(
        allManufacturerUsers.filter((user) =>
          [
            user.FirstName,
            user.LastName,
            user.Name,
            user.Email,
            user.Phone,
            user.ManufacturerName,
          ]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term)),
        ),
      );
      return;
    }
    if (selectedSegment === "Account Users") {
      if (val === "") {
        setAccountUsers(allAccUsers);
        return;
      }
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `search?screen=UsersDashboard&type=AccountUsers&word=${val}`,
      );
      const tempData = response?.data?.data?.map((user) => ({
        ...user,
        Name: formatName(user),
      }));
      setAccountUsers(tempData);
    } else {
      if (val === "") {
        setDistributorUsers(allDistUsers);
        return;
      }
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `search?screen=UsersDashboard&type=DistributorUsers&word=${val}`,
      );
      const tempData = response?.data?.data?.map((user) => ({
        ...user,
        Name: formatName(user),
      }));
      setDistributorUsers(tempData);
    }
  };

  if (!canViewUsers) return null;

  return (
    <div>
      <TitleBar
        label="User Dashboard"
        filterBarComponents={
          <>
            <Button
              variant="timeframe"
              isActive={selectedSegment === "AS Users"}
              onClick={() => handleSegmentChange("AS Users")}
            >
              AS Users
            </Button>
            <Button
              variant="timeframe"
              isActive={selectedSegment === "Account Users"}
              onClick={() => handleSegmentChange("Account Users")}
            >
              Account Users
            </Button>
            <Button
              variant="timeframe"
              isActive={selectedSegment === "Distributor Users"}
              onClick={() => handleSegmentChange("Distributor Users")}
            >
              Distributor Users
            </Button>
            <Button
              variant="timeframe"
              isActive={selectedSegment === "Manufacturer Users"}
              onClick={() => handleSegmentChange("Manufacturer Users")}
            >
              Manufacturer Users
            </Button>
          </>
        }
        rightFilterBarComponents={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {showRestore && (
              <AntButton onClick={() => setRestoreOpen(true)}>
                Restore deleted user
              </AntButton>
            )}
            <Input.Search
              placeholder="Search"
              allowClear={() => {
                if (selectedSegment === "AS Users") {
                  setAsUsers(allAsUsers);
                } else if (selectedSegment === "Account Users") {
                  setAccountUsers(allAccUsers);
                } else if (selectedSegment === "Manufacturer Users") {
                  setManufacturerUsers(allManufacturerUsers);
                } else {
                  setDistributorUsers(allDistUsers);
                }
              }}
              style={{ height: "36px" }}
              onSearch={(value) => handleSearch(value)}
            />
            {selectedSegment === "AS Users" && (
              <ASUserAddAction
                canAddUser={canAdminPerformUserAction(
                  user,
                  "manage_as_users.add",
                )}
                onUserAdded={getASUsersData}
              />
            )}
          </div>
        }
      />

      {showRestore && (
        <RestoreUserAction
          actor={user}
          open={restoreOpen}
          onClose={() => setRestoreOpen(false)}
          onRestored={getUsersData}
        />
      )}
      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      {selectedSegment === "AS Users" && !loading && (
        <GenericDashboard data={asUsers} screen="ASUsersDashboard" />
      )}
      {selectedSegment === "Account Users" && !loading && (
        <GenericDashboard data={accountUsers} screen="AccountUserDashboard" />
      )}
      {selectedSegment === "Distributor Users" && !loading && (
        <GenericDashboard
          data={distributorUsers}
          screen="DistributorUserDashboard"
        />
      )}
      {selectedSegment === "Manufacturer Users" && !loading && (
        <GenericDashboard data={manufacturerUsers} screen="ManufacturerUsers" />
      )}
    </div>
  );
};

export default UserDashboard;
